/**
 * Authorization: what a role may do, as CASL abilities.
 *
 * **Authentication and authorization are separate concerns here, and deliberately
 * separate libraries.** Better Auth owns who someone is, which organisation they are
 * acting in, and what role they hold there; CASL owns what that role may *do*. The
 * role is a label on a `members` row — Better Auth's business. The abilities are this
 * file's business, and nothing else decides them.
 *
 * **One source of truth.** `PERMISSION_MINIMUM_ROLE` is the policy. `permissionsFor`
 * reads it, `defineAbilityFor` turns it into a CASL ability, and `can` asks the
 * ability. There is no second table to drift, and a route cannot be granted something
 * the UI would hide, because both ask the same ability.
 *
 * **Isomorphic by construction.** The console imports `defineAbilityFor` and calls
 * `ability.can(action, subject)`; the API middleware does the same. That is the
 * "isomorphic RBAC in UI and middleware" the stack spec asks for, and it is why the
 * earlier decision to stay off CASL was revisited.
 *
 * **Subjects, not strings.** A permission is `resource:action` — the vocabulary routes
 * and the console already speak — and it maps onto a CASL subject and action. Doing
 * it that way rather than `can('templates:write', 'all')` is what leaves room for the
 * thing CASL is actually for later: conditional rules, such as "may write a template
 * in their own organisation". The string vocabulary stays because a dozen routes and
 * every stored role already use it.
 *
 * ## The four roles, and why the keys are not the names
 *
 * The names people read — Super Administrator, Administrator, IT Officer, Auditor —
 * are `ROLE_LABEL`. The keys Better Auth stores are `owner`, `admin`, `it-officer` and
 * `auditor`, and the first two are **not** free choices:
 *
 *   * `owner` is Better Auth's `creatorRole` and the role its own membership routes
 *     treat specially. `migrations/0003` predates this change, the plugin's
 *     "you cannot leave the organisation without an owner" guard is written against
 *     the literal `owner`, and `allowCreatorAllPermissions` means the organisation's
 *     creator is never locked out of its own settings. Renaming the key to
 *     `super-administrator` would delete all three properties, so the key stays and
 *     the label carries the name the product uses.
 *   * `admin` is likewise the key the plugin's default role list and its
 *     `member: ["create", "update", "delete"]` grants are written against.
 *
 * **The last-owner guarantee is the plugin's, not ours.** "There must always be at
 * least one Super Administrator" is enforced inside Better Auth's
 * `remove-member` and `update-member-role` routes, which count the organisation's
 * `owner` rows before writing. Re-implementing that count here would be a second,
 * weaker copy of a rule that already has no bypass — the plugin's version runs on the
 * path every caller must take, including ours.
 */
import { AbilityBuilder, createMongoAbility } from '@casl/ability'
import type { MongoAbility } from '@casl/ability'
import { createAccessControl } from 'better-auth/plugins/access'

/**
 * What may be acted on.
 *
 * These are the product's resources, not the schema's tables. Authorization is about
 * intent — "may this person publish a template?" — and binding it to tables would
 * make every migration an authorization change.
 */
export type AppSubject = 'Template' | 'Identity' | 'Connection' | 'Delivery' | 'Campaign' | 'Setting' | 'Audit' | 'Member' | 'Billing'

/** What may be done to a subject. */
export type AppAction = 'read' | 'write' | 'publish' | 'apply'

export type AppAbility = MongoAbility<[AppAction, AppSubject]>

/**
 * The resources and the actions each admits.
 *
 * The permission strings this repository has always used — `templates:read`,
 * `connections:write` — are derived from this, so the vocabulary has one author.
 * Adding an action here adds a permission everywhere at once; adding one by hand in
 * a route would not compile.
 */
export const STATEMENT = {
	templates: ['read', 'write', 'publish'],
	identities: ['read'],
	connections: ['read', 'write'],
	delivery: ['apply'],
	campaigns: ['write'],
	settings: ['write'],
	audit: ['read'],
	members: ['read', 'write'],
	billing: ['write'],
} as const

type Resource = keyof typeof STATEMENT
type ActionOf<R extends Resource> = (typeof STATEMENT)[R][number]

/** `resource:action`, for every action of every resource. */
export type Permission = { [R in Resource]: `${R}:${ActionOf<R>}` }[Resource]

/** Resource → the subject CASL is asked about. */
const SUBJECT_BY_RESOURCE: Record<Resource, AppSubject> = {
	templates: 'Template',
	identities: 'Identity',
	connections: 'Connection',
	delivery: 'Delivery',
	campaigns: 'Campaign',
	settings: 'Setting',
	audit: 'Audit',
	members: 'Member',
	billing: 'Billing',
}

/**
 * The four membership roles, as Better Auth stores them on a `member` row.
 *
 * Declared here rather than in `@layerit/db`, where it started: this module is what turns
 * a role into permissions, so it is where the vocabulary belongs, and the console — a
 * browser bundle — needs the type without pulling in a database package to get it.
 */
export type UserRole = 'owner' | 'admin' | 'it-officer' | 'auditor'

/**
 * The name a person reads, per role.
 *
 * Kept beside the key so the console, the invitation mail and the API's error text
 * cannot disagree about what `it-officer` is called. The keys are storage; these are
 * the product.
 */
export const ROLE_LABEL: Record<UserRole, string> = {
	owner: 'Super Administrator',
	admin: 'Administrator',
	'it-officer': 'IT Officer',
	auditor: 'Auditor',
}

/**
 * The one-line purpose of each role, for the picker.
 *
 * Written as what the role is *for* rather than as a permission list, because the
 * permission list is rendered separately from the same table the server enforces —
 * and a sentence that paraphrases it is a sentence that can go stale.
 */
export const ROLE_PURPOSE: Record<UserRole, string> = {
	owner: 'Full control, including the audit trail, billing and who may join. An organisation always keeps at least one.',
	admin: 'Runs the organisation day to day: signatures, rules, provider connections, and the team roster.',
	'it-officer': 'Sets up signatures and provider connections, and applies them. Cannot publish or change settings.',
	auditor: 'Reads everything and changes nothing — the audit trail included.',
}

export const ROLE_RANK: Record<UserRole, number> = {
	auditor: 0,
	'it-officer': 1,
	admin: 2,
	owner: 3,
}

/** Ordered least to most privileged, for UI pickers and for `hasRole`. */
export const ROLES: readonly UserRole[] = ['auditor', 'it-officer', 'admin', 'owner']

/**
 * The minimum role for each permission.
 *
 * This table **is** the policy. It works because the four roles nest: every
 * permission an Auditor holds an IT Officer holds too, and so on up to the Super
 * Administrator, who holds all of them. That nesting is asserted rather than assumed —
 * `tests/ability.test.ts` pins each role's exact permission set, and the API's suite
 * asserts that each role is a strict superset of the one below it — so an edit that
 * gives a junior role something a senior role lacks fails a test rather than shipping a
 * hierarchy the picker misrepresents.
 */
export const PERMISSION_MINIMUM_ROLE: Record<Permission, UserRole> = {
	/* Reading the organisation's own configuration. Everyone in it may. */
	'templates:read': 'auditor',
	'identities:read': 'auditor',
	'connections:read': 'auditor',

	/* The audit trail is readable by every role: an Auditor exists to read it, and an
	 * IT Officer who cannot see what changed cannot support the people who did. What
	 * separates them is the absence of writes, not the absence of sight. */
	'audit:read': 'auditor',

	/* Drafting work. An IT Officer sets signatures up; publishing them is above this. */
	'templates:write': 'it-officer',
	'campaigns:write': 'it-officer',

	/* Connecting a provider lets Layerit write to every mailbox in the tenant, and
	 * applying a signature writes to them directly. Both are the highest-blast-radius
	 * actions in the product, and both are the Administrator's. */
	'connections:write': 'admin',
	'delivery:apply': 'admin',
	'templates:publish': 'admin',
	'settings:write': 'admin',

	/* Reading the roster is how an IT Officer knows who they are setting up; changing
	 * it is who may join, which is an Administrator's call. */
	'members:read': 'it-officer',
	'members:write': 'admin',

	/* Money. Super Administrator only. */
	'billing:write': 'owner',
}

export function hasRole(role: UserRole, required: UserRole): boolean {
	return ROLE_RANK[role] >= ROLE_RANK[required]
}

/* ------------------------------------------------------------------ *
 * The IAM declaration, in Better Auth
 *
 * This is the system of record for *what a role means*: the statements and the four
 * roles built from them. `organization({ ac: accessControl, roles })` is what ties it
 * to membership.
 *
 * The product half is generated from `PERMISSION_MINIMUM_ROLE` rather than written out,
 * so the minimum-role table stays the only place a product policy is decided. Everything
 * downstream reads what these roles declare.
 * ------------------------------------------------------------------ */

/**
 * The resources Better Auth's own organization endpoints check, and the actions they
 * name.
 *
 * These are not the product's vocabulary — they are the library's, and they are here
 * because leaving them out makes the plugin refuse its own endpoints. Better Auth asks
 * `hasPermission({ role, permissions: { invitation: ['create'] } })` inside
 * `invite-member`, `organization: ['update']` inside `update-organization`, and so on,
 * and a role whose declared statements do not mention `invitation` at all answers "no"
 * to every one of them. The first symptom is the organisation's own creator being told
 * they are not allowed to invite anybody, which is how this was found: a missing
 * statement is not a compile error, it is a permission that silently exists nowhere.
 *
 * The actions are copied from the library's `defaultStatements` rather than imported,
 * because the values it ships are not exported from a stable path. `tests/ability.test.ts`
 * asserts that every endpoint the team screen calls is granted to the role that calls it,
 * which is what catches a change on either side.
 *
 * `ac` is the plugin's `dynamicAccessControl` resource: custom roles, stored as
 * `organization_role` rows. Every role may read them — the session resolver reads a
 * member's custom role through the plugin as that member — and the roles that manage
 * membership may create, update and delete them. The plugin additionally refuses any
 * role that grants something its author does not hold, so `ac:create` is never a way
 * to escalate.
 */
const PLUGIN_STATEMENT = {
	organization: ['update', 'delete'],
	member: ['create', 'update', 'delete'],
	invitation: ['create', 'cancel'],
	team: ['create', 'update', 'delete'],
	ac: ['create', 'read', 'update', 'delete'],
} as const

/** The library's resources, as a type of their own. */
type PluginResource = keyof typeof PLUGIN_STATEMENT

/**
 * What each role may do through Better Auth's own organization endpoints.
 *
 * The values are typed `readonly string[]` rather than per-resource literal unions, and
 * that is a decision: a mapped type over a union key intersects the value types, so
 * `organization: ['read']` would be rejected — `read` is granted on `organization` and
 * nowhere else, and an intersection keeps only the actions every resource admits.
 * `tests/ability.test.ts` is the check the type gives up, and it is the stricter one: it
 * tries every endpoint the team screen calls against the role that calls it.
 *
 * The plugin's three default roles are not usable as they are: `memberAc` holds nothing,
 * and neither the IT Officer nor the Auditor is a `member`. These four are written out,
 * worst to best, so the ladder holds on both halves — a role that may invite may also
 * read, and only the Super Administrator may delete the organisation.
 *
 * `organization: ['read']` on the lower two is **declared rather than relied upon**:
 * Better Auth checks only the actions in `PLUGIN_STATEMENT`, so this grants nothing there.
 * It is the honest answer to "may this role read the organisation", and it is what the
 * ability check asks when the console renders the organisation's own details.
 */
const PLUGIN_GRANTS: Record<UserRole, Record<PluginResource, readonly string[]>> = {
	auditor: { organization: ['read'], member: [], invitation: [], team: [], ac: ['read'] },
	'it-officer': { organization: ['read'], member: [], invitation: [], team: [], ac: ['read'] },
	admin: {
		organization: ['read', 'update'],
		member: ['create', 'update', 'delete'],
		invitation: ['create', 'cancel'],
		team: ['create', 'update', 'delete'],
		ac: ['create', 'read', 'update', 'delete'],
	},
	owner: {
		organization: ['read', 'update', 'delete'],
		member: ['create', 'update', 'delete'],
		invitation: ['create', 'cancel'],
		team: ['create', 'update', 'delete'],
		ac: ['create', 'read', 'update', 'delete'],
	},
}

/**
 * The statement `createAccessControl` is built from: the product's resources and the
 * library's, merged.
 *
 * One `Record` literal rather than a spread, so every product permission the routes and
 * the console speak stays in the compile-time type. Composing it here is what lets one
 * `newRole` call carry both halves — and one access-control object is what makes
 * `roles[role].authorize({ invitation: ['create'] })` answerable at all.
 */
export const MERGED_STATEMENT: Record<Resource | PluginResource, readonly string[]> = {
	templates: STATEMENT.templates,
	identities: STATEMENT.identities,
	connections: STATEMENT.connections,
	delivery: STATEMENT.delivery,
	campaigns: STATEMENT.campaigns,
	settings: STATEMENT.settings,
	audit: STATEMENT.audit,
	members: STATEMENT.members,
	billing: STATEMENT.billing,
	organization: PLUGIN_STATEMENT.organization,
	member: PLUGIN_STATEMENT.member,
	invitation: PLUGIN_STATEMENT.invitation,
	team: PLUGIN_STATEMENT.team,
	ac: PLUGIN_STATEMENT.ac,
}

export const accessControl = createAccessControl(MERGED_STATEMENT)

/**
 * The grants a role holds, read off the minimum-role table and the plugin map.
 *
 * Assembled by key at runtime, so the compiler checks the shape but cannot check that
 * the *values* are right. `tests/ability.test.ts` covers that with the policy written
 * out by hand.
 */
function grantsFor(role: UserRole): Record<string, string[]> {
	const grants: Record<string, string[]> = {}

	for (const permission of seedPermissions(role)) {
		const [resource, action] = permission.split(':') as [string, string]
		grants[resource] = [...(grants[resource] ?? []), action]
	}

	for (const [resource, actions] of Object.entries(PLUGIN_GRANTS[role])) {
		grants[resource] = [...(grants[resource] ?? []), ...actions]
	}

	return grants
}

/** The seed: which product permissions a role holds, before Better Auth is asked. */
function seedPermissions(role: UserRole): Permission[] {
	return (Object.keys(PERMISSION_MINIMUM_ROLE) as Permission[]).filter((permission) => hasRole(role, PERMISSION_MINIMUM_ROLE[permission]))
}

/**
 * The four membership roles, as Better Auth roles. `members.role` holds one of these.
 *
 * `owner` and `admin` keep their keys — see the file header — so the plugin's own
 * permission model (`owner` is all-permitted, `admin` may manage members) lines up
 * with this table instead of contradicting it.
 */
export const roles = {
	auditor: accessControl.newRole(grantsFor('auditor')),
	'it-officer': accessControl.newRole(grantsFor('it-officer')),
	admin: accessControl.newRole(grantsFor('admin')),
	owner: accessControl.newRole(grantsFor('owner')),
}

/* ------------------------------------------------------------------ *
 * The bridge: IAM data → CASL abilities
 * ------------------------------------------------------------------ */

/**
 * Every permission a role declares, read back out of Better Auth.
 *
 * **Read, not re-derived.** This is the one seam between the two libraries: the role
 * objects above are the IAM data, and this turns them into the vocabulary CASL checks
 * against. Reading them rather than recomputing from `PERMISSION_MINIMUM_ROLE` is what
 * keeps a role changed through Better Auth from being ignored — which is why the data
 * lives there.
 */
export function permissionsFor(role: UserRole): Permission[] {
	const declared = roles[role].statements as Record<string, readonly string[]>

	return (Object.keys(PERMISSION_MINIMUM_ROLE) as Permission[]).filter((permission) => {
		const [resource, action] = permission.split(':') as [string, string]
		return declared[resource]?.includes(action) ?? false
	})
}

/**
 * Whether a role declares one of Better Auth's own organization permissions — the
 * plugin's vocabulary rather than the product's.
 *
 * The API does not need this to guard a route: `organization({ ac, roles })` is what
 * makes Better Auth check these on its own endpoints, and those checks run before any
 * handler of ours. It exists so a test can assert the one thing that is easy to get
 * silently wrong — that the role which calls an endpoint holds the permission the
 * endpoint demands — and so a future change to either half has something to fail.
 */
export function pluginPermission(role: UserRole, resource: PluginResource, action: string): boolean {
	/* The array is guaranteed to exist: every role declares all five plugin resources, and
	 * `tests/ability.test.ts` asserts that totality directly. A runtime fallback would hide
	 * the one failure this function exists to catch — a role that forgot the library's
	 * vocabulary and would therefore be refused by Better Auth's own endpoints. */
	const declared = roles[role].statements as Record<string, readonly string[]>
	/* `!` because the widening above is what makes the assertion necessary: `declared` is a
	 * `Record<string, …>`, so every read is `T | undefined` to the compiler even though this
	 * one cannot be. The alternative is a mapped type over `PluginResource` that Better
	 * Auth's own role objects do not satisfy. */
	return declared[resource]!.includes(action)
}

/** `templates:write` → `['write', 'Template']`. */
export function subjectAndAction(permission: Permission): [AppSubject, AppAction] {
	const [resource, action] = permission.split(':') as [Resource, AppAction]
	return [SUBJECT_BY_RESOURCE[resource], action]
}

/**
 * Builds an ability from an explicit permission list.
 *
 * This is the seam the console uses: `/v1/admin/me` returns the caller's permissions
 * and the UI builds the same ability the server enforced with, rather than
 * re-implementing the policy in the browser.
 */
export function abilityFromPermissions(permissions: readonly Permission[]): AppAbility {
	const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility)
	for (const permission of permissions) {
		const [subject, action] = subjectAndAction(permission)
		can(action, subject)
	}
	return build()
}

/* Four roles, four abilities, built once. `can` is asked on every guarded request, so
 * rebuilding per call would put a rule compile in the hot path for no reason. */
const abilities = new Map<UserRole, AppAbility>()

export function defineAbilityFor(role: UserRole): AppAbility {
	const existing = abilities.get(role)
	if (existing) return existing

	const ability = abilityFromPermissions(permissionsFor(role))
	abilities.set(role, ability)
	return ability
}

/**
 * The question routes and the console ask: may this role do this?
 *
 * Answered by the ability, which was built from what Better Auth declares — so the
 * two cannot disagree, and a role edited at runtime is answered with its new
 * permissions rather than the ones it shipped with.
 */
export function can(role: UserRole, permission: Permission): boolean {
	const [subject, action] = subjectAndAction(permission)
	return defineAbilityFor(role).can(action, subject)
}

/** Whether a string is one of the four roles, for anything arriving untyped. */
export function isUserRole(value: string): value is UserRole {
	return (ROLES as readonly string[]).includes(value)
}

/* ------------------------------------------------------------------ *
 * Custom roles
 *
 * The four roles above are the defaults: code, identical in every organisation, and not
 * editable. An organisation that needs another cut defines it through Better Auth's
 * `dynamicAccessControl` — an `organization_role` row holding a statement in the same
 * vocabulary — and the plugin enforces it on its own endpoints exactly as it does the
 * built-in ones. These helpers are the two directions of that crossing: a permission
 * list into a statement the plugin stores, and a stored statement back into the
 * permission list CASL is built from.
 * ------------------------------------------------------------------ */

/** Every product permission, in the minimum-role table's order. */
export const ALL_PERMISSIONS: readonly Permission[] = Object.keys(PERMISSION_MINIMUM_ROLE) as Permission[]

/** Whether a string is a product permission, for anything arriving untyped. */
export function isPermission(value: string): value is Permission {
	return (ALL_PERMISSIONS as readonly string[]).includes(value)
}

/**
 * The statement Better Auth stores for a custom role holding `permissions`.
 *
 * The product half is the list itself. The plugin half is derived rather than chosen, so a
 * custom role can never hold a plugin grant its product permissions do not explain:
 *
 *   * every role may read the organisation and its roles — the resolver reads a member's
 *     own custom role through the plugin, as them, and that read needs `ac:read`;
 *   * `members:write` is "decides who may join and what they may do", which is the
 *     plugin's member, invitation, team and role-management grants together;
 *   * `settings:write` is the organisation's own details.
 *
 * Deleting the organisation is not derivable: it stays the Super Administrator's alone.
 */
export function statementFor(permissions: readonly Permission[]): Record<string, string[]> {
	const held = new Set(permissions)
	const statement: Record<string, string[]> = {}

	for (const permission of ALL_PERMISSIONS) {
		if (!held.has(permission)) continue
		const [resource, action] = permission.split(':') as [string, string]
		statement[resource] = [...(statement[resource] ?? []), action]
	}

	statement.organization = held.has('settings:write') ? ['read', 'update'] : ['read']
	statement.ac = held.has('members:write') ? ['create', 'read', 'update', 'delete'] : ['read']
	if (held.has('members:write')) {
		statement.member = ['create', 'update', 'delete']
		statement.invitation = ['create', 'cancel']
		statement.team = ['create', 'update', 'delete']
	}
	return statement
}

/**
 * The product permissions a stored statement grants — the read-back `permissionsFor` does
 * for a built-in role, for a row of any provenance.
 *
 * Anything that is not a product permission is ignored rather than refused: the plugin's
 * own grants live in the same statement, and a row is only ever as strong as the actions
 * it names that this table also knows.
 */
export function permissionsFromStatement(statement: Readonly<Record<string, readonly string[]>>): Permission[] {
	return ALL_PERMISSIONS.filter((permission) => {
		const [resource, action] = permission.split(':') as [string, string]
		return statement[resource]?.includes(action) ?? false
	})
}

/**
 * The storage key for a custom role named `label`: lowercase, hyphenated, ASCII.
 *
 * The plugin lowercases a role name before storing it, and refuses one that collides with
 * a built-in role or an existing row, so this only has to produce something stable and
 * readable in a log. The key never changes after creation — renaming edits the label —
 * so a member's assignment survives a rename.
 */
export function customRoleKey(label: string): string {
	const slug = label
		.normalize('NFKD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 48)
	return slug.length > 0 ? slug : 'role'
}
